import * as DMath from "./det-math";
import type { Vec2 } from "./vec2";

/** Size of a wrap-around (toroidal) world whose origin is (0, 0). */
export interface WorldSize {
	width: number;
	height: number;
}

/** Wraps a coordinate into [0, size) (full modulo, handles any distance). */
export function wrapCoord(value: number, size: number): number {
	const v = value % size;
	return v < 0 ? v + size : v;
}

export function wrapPosition(p: Vec2, size: WorldSize): Vec2 {
	return { x: wrapCoord(p.x, size.width), y: wrapCoord(p.y, size.height) };
}

/** Shortest signed offset from `from` to `to` along one axis of length `size`. */
export function wrappedAxisDelta(from: number, to: number, size: number): number {
	let d = (to - from) % size;
	if (d > size / 2) d -= size;
	else if (d < -size / 2) d += size;
	return d;
}

/** Shortest vector from `from` to `to` on the torus (used for rendering and wrap-aware physics). */
export function wrappedDelta(from: Vec2, to: Vec2, size: WorldSize): Vec2 {
	return {
		x: wrappedAxisDelta(from.x, to.x, size.width),
		y: wrappedAxisDelta(from.y, to.y, size.height),
	};
}

export function wrappedDistance(a: Vec2, b: Vec2, size: WorldSize): number {
	const d = wrappedDelta(a, b, size);
	return DMath.hypot(d.x, d.y);
}
